import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCipherStore } from '../store/cipherStore';
import { validateJSON } from '../utils';

const CipherLayerCard = ({ layer, index }) => {
  const { removeLayer, updateLayerKey, toggleLayer } = useCipherStore();
  const [isEditing, setIsEditing] = useState(false);
  const [keyValue, setKeyValue] = useState(layer.key || '');
  const [keyError, setKeyError] = useState('');
  
  const handleKeyChange = (e) => {
    const newValue = e.target.value;
    setKeyValue(newValue);
    
    // Validate key based on type
    if (layer.cipher.keyType === 'number') {
      const num = parseInt(newValue);
      if (isNaN(num) || num < 0 || num > 25) {
        setKeyError('Must be a number between 0-25');
      } else {
        setKeyError('');
      }
    } else if (layer.cipher.keyType === 'textarea') {
      const validation = validateJSON(newValue);
      if (newValue && !validation.valid) {
        setKeyError('Invalid JSON format');
      } else {
        setKeyError('');
      }
    } else {
      setKeyError('');
    }
  };
  
  const handleSaveKey = () => {
    if (!keyError) {
      let processedKey = keyValue;
      
      // Process key based on type
      if (layer.cipher.keyType === 'number') {
        processedKey = parseInt(keyValue);
      } else if (layer.cipher.keyType === 'textarea' && keyValue) {
        try {
          processedKey = JSON.parse(keyValue);
        } catch (error) {
          processedKey = {};
        }
      }
      
      updateLayerKey(layer.id, processedKey);
      setIsEditing(false);
    }
  };
  
  const handleCancelEdit = () => {
    setKeyValue(layer.key || '');
    setKeyError('');
    setIsEditing(false);
  };
  
  const getCipherIcon = (type) => {
    const icons = {
      caesar: '🏛️',
      rot13: '🔄',
      base64: '📦',
      xor: '⚡',
      custom: '🎨'
    };
    return icons[type] || '🔐';
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: -20 }}
      whileHover={{ scale: 1.02 }}
      className={`cyber-border rounded-lg p-4 space-y-3 ${
        layer.isActive ? 'border-neon-cyan/50' : 'border-gray-600/50 opacity-60'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{getCipherIcon(layer.type)}</span>
          <div>
            <h3 className="font-semibold text-neon-cyan">{layer.cipher.name}</h3>
            <p className="text-xs text-gray-400">{layer.cipher.description}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => toggleLayer(layer.id)}
            className={`p-2 rounded-lg transition-colors ${
              layer.isActive 
                ? 'bg-neon-cyan/20 text-neon-cyan' 
                : 'bg-gray-700 text-gray-400'
            }`}
            title={layer.isActive ? 'Disable layer' : 'Enable layer'}
          >
            {layer.isActive ? '👁️' : '👁️‍🗨️'}
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => removeLayer(layer.id)}
            className="p-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 rounded-lg transition-colors"
            title="Remove layer"
          >
            🗑️
          </motion.button>
        </div>
      </div>
      
      {layer.cipher.requiresKey && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-300">{layer.cipher.keyLabel}:</label>
            {!isEditing && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsEditing(true)}
                className="text-xs text-neon-cyan hover:text-neon-cyan/80"
              >
                ✏️ Edit
              </motion.button>
            )}
          </div>
          
          <AnimatePresence mode="wait">
            {isEditing ? (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2"
              >
                {layer.cipher.keyType === 'textarea' ? (
                  <textarea
                    value={keyValue}
                    onChange={handleKeyChange}
                    placeholder={`Enter ${layer.cipher.keyLabel.toLowerCase()}`}
                    className="cyber-input w-full p-2 text-sm rounded h-20 resize-none"
                  />
                ) : (
                  <input
                    type={layer.cipher.keyType === 'number' ? 'number' : 'text'}
                    value={keyValue}
                    onChange={handleKeyChange}
                    placeholder={`Enter ${layer.cipher.keyLabel.toLowerCase()}`}
                    className="cyber-input w-full p-2 text-sm rounded"
                    min={layer.cipher.keyType === 'number' ? 0 : undefined}
                    max={layer.cipher.keyType === 'number' ? 25 : undefined}
                  />
                )}
                
                {keyError && (
                  <p className="text-xs text-red-400">{keyError}</p>
                )}
                
                <div className="flex gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSaveKey}
                    disabled={!!keyError}
                    className="cyber-button text-xs px-3 py-1 disabled:opacity-50"
                  >
                    ✓ Save
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCancelEdit}
                    className="cyber-button text-xs px-3 py-1"
                  >
                    ✕ Cancel
                  </motion.button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="cyber-input p-2 text-sm rounded bg-gray-800/50"
              >
                {layer.key !== null && layer.key !== undefined 
                  ? (typeof layer.key === 'object' 
                      ? JSON.stringify(layer.key) 
                      : layer.key.toString())
                  : 'No key set'
                }
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>Layer {index + 1}</span>
        <span className={`px-2 py-1 rounded ${
          layer.isActive ? 'bg-green-900/30 text-green-400' : 'bg-gray-700/30 text-gray-400'
        }`}>
          {layer.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>
    </motion.div>
  );
};

export default CipherLayerCard;
